// frontend/src/components/StepAddress.jsx
import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Tooltip,
  Box,
  Typography,
  Autocomplete,
} from "@mui/material";
import api from "../utils/api"; // 👈 make sure you have api.js (axios instance)

// 🔹 StepAddress Component
const StepAddress = ({ data, onNext, onBack }) => {
  const [form, setForm] = useState({
    address: data.address || "",
    city: data.city || "",
    country: data.country || "",
    postal_code: data.postal_code || "",
  });

  const [errors, setErrors] = useState({});
  const [tooltip, setTooltip] = useState("");
  const [countries, setCountries] = useState([]);

  // ✅ Fetch countries from backend
  useEffect(() => {
    api
      .get("/api/countries")
      .then((res) => {
        // assuming Laravel returns { data: [...] }
        const countryList = res.data.data || res.data;
        setCountries(
          countryList.map((c) => ({
            code: c.code, // ISO Alpha-2 code
            label: c.name, // country name
            phone: c.phone_code, // we can use later for phone input
          }))
        );
      })
      .catch((err) => {
        console.error("Error fetching countries:", err);
      });
  }, []);

  // ✅ Postal code regex validation
  const isPostalValid = /^[A-Za-z0-9]{3,10}$/.test(form.postal_code);

  // ✅ Update tooltip whenever postal_code changes
  useEffect(() => {
    if (!form.postal_code) {
      setTooltip("");
      return;
    }
    if (isPostalValid) {
      setTooltip("✔ Valid postal code");
    } else {
      setTooltip("✖ Invalid postal code");
    }
  }, [form.postal_code, isPostalValid]);

  const validate = () => {
    let newErrors = {};
    if (!form.address) newErrors.address = "Street address required";
    if (!form.city) newErrors.city = "City required";
    if (!form.country) newErrors.country = "Country required";
    if (!form.postal_code || !isPostalValid) {
      newErrors.postal_code = "Valid postal code required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleNext = () => {
    if (validate()) {
      onNext(form);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="h6" align="center">
        Address Details
      </Typography>

      <TextField
        name="address"
        label="Street Address"
        value={form.address}
        onChange={handleChange}
        error={!!errors.address}
        helperText={errors.address}
        fullWidth
      />

      <TextField
        name="city"
        label="City"
        value={form.city}
        onChange={handleChange}
        error={!!errors.city}
        helperText={errors.city}
        fullWidth
      />

      <Autocomplete
        options={countries}
        getOptionLabel={(option) => option.label}
        value={countries.find((c) => c.label === form.country) || null}
        onChange={(e, newValue) =>
          setForm({ ...form, country: newValue ? newValue.label : "" })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Country"
            error={!!errors.country}
            helperText={errors.country}
            fullWidth
          />
        )}
      />

      <Tooltip
        title={tooltip}
        placement="right"
        open={Boolean(form.postal_code)}
        disableHoverListener
      >
        <TextField
          name="postal_code"
          label="Postal Code"
          value={form.postal_code}
          onChange={handleChange}
          error={!!errors.postal_code}
          helperText={errors.postal_code}
          fullWidth
        />
      </Tooltip>

      {/* ✅ Map Preview */}
      {form.postal_code && isPostalValid && (
        <Box
          sx={{
            mt: 2,
            height: 200,
            borderRadius: 2,
            overflow: "hidden",
            border: "2px solid #00f0ff",
          }}
        >
          <iframe
            title="map-preview"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            loading="lazy"
            allowFullScreen
            src={`https://www.google.com/maps?q=${encodeURIComponent(
              form.postal_code + " " + form.city
            )}&output=embed`}
          />
        </Box>
      )}

      {/* Buttons */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
        <Button
          onClick={onBack}
          variant="outlined"
          sx={{
            borderRadius: "2rem",
            px: 4,
            borderColor: "#00f0ff",
            color: "#00f0ff",
            "&:hover": { borderColor: "#ff00f0", color: "#ff00f0" },
          }}
        >
          Back
        </Button>

        <Button
          onClick={handleNext}
          variant="contained"
          sx={{
            borderRadius: "2rem",
            px: 4,
            background: "linear-gradient(90deg, #00f0ff, #ff00f0)",
            color: "#fff",
            "&:hover": {
              background: "linear-gradient(90deg, #ff00f0, #00f0ff)",
            },
          }}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};

export default StepAddress;